import { useEffect, useMemo, useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { normalizeStringList } from '@/utils/normalizeStringList'

export type BillCustomerTextComboboxProps = {
  id?: string
  value: string
  onChange: (value: string) => void
  suggestions: string[]
  loading?: boolean
  placeholder?: string
  disabled?: boolean
  emptyHint?: string
  onSelectOption?: (value: string) => void
  className?: string
}

export function BillCustomerTextCombobox({
  id,
  value,
  onChange,
  suggestions,
  loading = false,
  placeholder,
  disabled,
  emptyHint,
  onSelectOption,
  className,
}: BillCustomerTextComboboxProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState(value)
  const containerRef = useRef<HTMLDivElement>(null)
  const safeSuggestions = normalizeStringList(suggestions)

  useEffect(() => {
    setSearch(value)
  }, [value])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return safeSuggestions
    return safeSuggestions.filter((s) => s.toLowerCase().includes(q))
  }, [safeSuggestions, search])

  const qTrim = search.trim()
  const showUseCustom =
    qTrim.length > 0 && !filtered.some((s) => s.toLowerCase() === qTrim.toLowerCase())

  const selectValue = (next: string) => {
    onChange(next)
    setSearch(next)
    onSelectOption?.(next)
    setOpen(false)
  }

  const showDropdown =
    open && !disabled && (loading || filtered.length > 0 || showUseCustom || !!emptyHint)

  return (
    <div ref={containerRef} className="relative w-full">
      <Input
        id={id}
        className={cn('h-9 w-full border-border/80 bg-background text-sm', className)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        value={search}
        onChange={(e) => {
          const next = e.target.value
          setSearch(next)
          onChange(next)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          window.setTimeout(() => setOpen(false), 150)
        }}
      />
      {showDropdown ? (
        <div
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[min(50vh,280px)] overflow-y-auto rounded-md border border-border/80 bg-popover p-1 text-popover-foreground shadow-md"
          role="listbox"
        >
          {loading ? (
            <p className="px-3 py-4 text-center text-sm text-muted-foreground">กำลังโหลด...</p>
          ) : null}
          {!loading && filtered.length === 0 && !showUseCustom ? (
            <p className="px-3 py-4 text-center text-sm text-muted-foreground">
              {emptyHint ?? 'ไม่พบรายการ'}
            </p>
          ) : null}
          {filtered.map((item) => (
            <button
              key={item}
              type="button"
              className={cn(
                'flex w-full cursor-pointer rounded-sm px-2 py-1.5 text-left text-sm outline-none',
                'hover:bg-accent hover:text-accent-foreground',
                value === item && 'bg-accent text-accent-foreground',
              )}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectValue(item)}
            >
              {item}
            </button>
          ))}
          {showUseCustom ? (
            <button
              type="button"
              className="flex w-full cursor-pointer rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectValue(qTrim)}
            >
              ใช้ &quot;{qTrim}&quot;
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
